import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { SEGMENT_LABELS, SEGMENT_COUNTS, saveCampaign, useEmailTemplates, } from '../../../http/crm/email-campaigns';
import { Instant, LocalDateTime, DateTimeFormatter, ZoneId } from '../../../utility/datetime';
import CModal from '../modal';
import CInput from '../input';
import CSelect from '../select';
import Button from '../button';
// datetime-local uses `yyyy-MM-dd'T'HH:mm` in the browser's local zone.
// Round-trip: ISO Instant <-> LocalDateTime in ZoneId.SYSTEM <-> that format.
const LOCAL_INPUT_FORMAT = DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm");
const toLocalInput = (iso) => {
    if (!iso) {
        return '';
    }
    return Instant.parse(iso).atZone(ZoneId.SYSTEM).toLocalDateTime().format(LOCAL_INPUT_FORMAT);
};
const fromLocalInput = (v) => LocalDateTime.parse(v, LOCAL_INPUT_FORMAT).atZone(ZoneId.SYSTEM).toInstant().toString();
const SEGMENTS = ['all_customers', 'inactive_30_days', 'due_for_service', 'recent_invoice'];
const formatStat = (value, total) => {
    if (total === 0) {
        return `${value}`;
    }
    return `${value} (${Math.round(value / total * 100)}%)`;
};
const blank = () => ({
    'name': '',
    'template_id': null,
    'subject': '',
    'body': '',
    'segment': 'all_customers',
    'schedule_mode': 'now',
    'scheduled_for': '',
});
const fromCampaign = (c) => ({
    'name': c.name,
    'template_id': c.template_id,
    'subject': c.subject,
    'body': c.body,
    'segment': c.segment,
    'schedule_mode': c.status === 'scheduled' ? 'schedule' : c.status === 'draft' ? 'draft' : 'now',
    'scheduled_for': toLocalInput(c.scheduled_for),
});
export default (props) => {
    const templates = useEmailTemplates();
    const [form, setForm] = React.useState(blank());
    const isView = !!props.campaign && (props.campaign.status === 'sent' || props.campaign.status === 'sending' || props.campaign.status === 'failed');
    React.useEffect(() => {
        if (props.visible) {
            setForm(props.campaign ? fromCampaign(props.campaign) : blank());
        }
    }, [props.visible, props.campaign]);
    const set = (key) => (value) => setForm((s) => (Object.assign(Object.assign({}, s), { [key]: value })));
    const applyTemplate = (id) => {
        if (!id) {
            setForm((s) => (Object.assign(Object.assign({}, s), { 'template_id': null })));
            return;
        }
        const t = templates.find((x) => x.id === id);
        if (!t) {
            return;
        }
        setForm((s) => (Object.assign(Object.assign({}, s), { 'template_id': id, 'subject': t.subject, 'body': t.body })));
    };
    const canSubmit = !isView
        && form.name.trim().length > 0
        && form.subject.trim().length > 0
        && form.body.trim().length > 0
        && (form.schedule_mode !== 'schedule' || form.scheduled_for.length > 0);
    const handleSubmit = (done) => {
        var _a;
        if (isView) {
            done();
            return;
        }
        if (!canSubmit) {
            done();
            return;
        }
        const status = form.schedule_mode === 'draft' ? 'draft' :
            form.schedule_mode === 'schedule' ? 'scheduled' :
                'sent';
        const saved = saveCampaign({
            'id': (_a = props.campaign) === null || _a === void 0 ? void 0 : _a.id,
            'name': form.name.trim(),
            'template_id': form.template_id,
            'subject': form.subject.trim(),
            'body': form.body,
            'segment': form.segment,
            'status': status,
            'scheduled_for': form.schedule_mode === 'schedule' ? fromLocalInput(form.scheduled_for) : null,
        });
        props.onSaved(saved);
        done();
    };
    const audience = SEGMENT_COUNTS[form.segment];
    const actionLabel = isView ? 'Close' :
        form.schedule_mode === 'draft' ? 'Save Draft' :
            form.schedule_mode === 'schedule' ? 'Schedule Send' :
                `Send to ${audience} Customers`;
    return _jsxs(CModal, { visible: props.visible, title: isView ? 'Campaign Details' : props.campaign ? 'Edit Campaign' : 'New Campaign', onClose: props.onClose, onSubmit: isView ? (done) => { props.onClose(); done(); } : handleSubmit, action: actionLabel, lg: true, disabled: !isView && !canSubmit, children: [!isView && (_jsx("p", { className: "text-muted small mb-3", children: "Compose an email and pick which customers receive it. You can save as a draft, schedule it, or send right now." })), _jsx(CInput, { type: "text", value: form.name, onChange: set('name'), required: true, disabled: isView, children: "Campaign Name" }), !isView && (_jsx(CSelect, { items: templates, valuef: (t) => t.id, titlef: (t) => t.name, value: form.template_id, onChange: (v) => applyTemplate(v), emptyTitle: "(Compose from scratch)", children: "Start from Template" })), _jsx(CInput, { type: "text", value: form.subject, onChange: set('subject'), required: true, disabled: isView, children: "Subject" }), _jsx("label", { className: "form-label small text-muted", children: "Body" }), _jsx("textarea", { className: "form-control", rows: isView ? 8 : 10, value: form.body, onChange: (e) => set('body')(e.currentTarget.value), disabled: isView, placeholder: "Use {{first_name}} to personalize." }), _jsx("div", { className: "mt-3", children: _jsx(CSelect, { items: SEGMENTS, valuef: (s) => s, titlef: (s) => `${SEGMENT_LABELS[s]} (${SEGMENT_COUNTS[s]} customers)`, value: form.segment, onChange: (v) => set('segment')((v !== null && v !== void 0 ? v : 'all_customers')), required: true, disabled: isView, children: "Recipients" }) }), !isView && (_jsxs(_Fragment, { children: [_jsx("label", { className: "form-label small text-muted mt-2", children: "When to Send" }), _jsxs("div", { className: "d-flex flex-wrap gap-2 mb-2", children: [_jsx(Button, { color: form.schedule_mode === 'now' ? 'primary' : 'secondary', onClick: () => set('schedule_mode')('now'), children: "Send Now" }), _jsx(Button, { color: form.schedule_mode === 'schedule' ? 'primary' : 'secondary', onClick: () => set('schedule_mode')('schedule'), children: "Schedule" }), _jsx(Button, { color: form.schedule_mode === 'draft' ? 'primary' : 'secondary', onClick: () => set('schedule_mode')('draft'), children: "Save as Draft" })] }), form.schedule_mode === 'schedule' && (_jsx("input", { type: "datetime-local", className: "form-control", value: form.scheduled_for, onChange: (e) => set('scheduled_for')(e.currentTarget.value), required: true }))] })), isView && !!props.campaign && (_jsxs("div", { className: "mt-4", children: [_jsx("h6", { children: "Send Results" }), _jsx("table", { className: "table table-sm", children: _jsxs("tbody", { children: [_jsxs("tr", { children: [_jsx("td", { children: "Sent to" }), _jsxs("td", { className: "text-end", children: [_jsx("strong", { children: props.campaign.recipient_count }), " customers"] })] }), _jsxs("tr", { children: [_jsx("td", { children: "Delivered" }), _jsx("td", { className: "text-end", children: formatStat(props.campaign.stats.delivered, props.campaign.recipient_count) })] }), _jsxs("tr", { children: [_jsx("td", { children: "Opened" }), _jsx("td", { className: "text-end", children: formatStat(props.campaign.stats.opened, props.campaign.stats.delivered) })] }), _jsxs("tr", { children: [_jsx("td", { children: "Clicked" }), _jsx("td", { className: "text-end", children: formatStat(props.campaign.stats.clicked, props.campaign.stats.delivered) })] }), _jsxs("tr", { children: [_jsx("td", { children: "Bounced" }), _jsx("td", { className: "text-end", children: formatStat(props.campaign.stats.bounced, props.campaign.recipient_count) })] })] }) })] }))] });
};
